import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getLeagueById, removeUsersFromLeague } from "../../services/leagueService";
import { League } from "../../types/League";
import AlertSnackbar from "../../common/AlertSnackbar";
import { useUser } from "../../common/contexts/userContext";
import { useAllUsers } from "../../common/contexts/allUsersContext";
import "./LeagueStyles.scss";

interface RemoveUsersPanelProps {
  manageLeagueID: number;
  refetchData: boolean;
  isDesktop?: boolean;
}

export { getLeagueById as getUserLeagueById };

const RemoveUsersPanel: React.FC<RemoveUsersPanelProps> = ({
  manageLeagueID,
  refetchData,
  isDesktop,
}) => {
  const [league, setLeague] = useState<League | undefined>();
  const { user } = useUser();
  useEffect(() => {
    getLeagueById(manageLeagueID).then(setLeague);
  }, [manageLeagueID, refetchData]);

  const otherUserIds =
    league?.userIds?.filter((item) => user.id !== item) ?? [];

  const memberList = (
    <div className="removeUsers__list">
      {otherUserIds.map((userId, index) => (
        <RemoveUserTile
          userId={userId}
          manageLeagueID={manageLeagueID}
          key={`${index}- remove - user - tile`}
        />
      ))}
      {otherUserIds.length === 0 && (
        <Typography className="manageLeague__emptyText">
          No other members to remove yet.
        </Typography>
      )}
    </div>
  );

  // On desktop the parent already provides a "Members" panel + title, so render
  // the list directly. On mobile keep the collapsible accordion.
  if (isDesktop) {
    return memberList;
  }

  return (
    <Accordion className="removeUsers__accordion" disableGutters elevation={0}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className="removeUsers__expandIcon" />}
        aria-controls="remove-users-content"
        id="remove-users-header"
        className="removeUsers__summary"
      >
        <Typography className="removeUsers__title">
          Remove users from {league?.name}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{memberList}</AccordionDetails>
    </Accordion>
  );
};

interface RemoveUserTileProps {
  userId: string;
  manageLeagueID: number;
}

const RemoveUserTile: React.FC<RemoveUserTileProps> = ({
  userId,
  manageLeagueID,
}) => {

  const [showAlertRemove, setShowAlertRemove] = useState(false);
  const [label, setLabel] = useState("Remove");
  const {users} = useAllUsers();
  const currentUser = users.filter((user) => user.id === userId)[0];
  function handleClick() {
    removeUsersFromLeague(manageLeagueID, [userId])
      .then(() => {
        setShowAlertRemove(true);
        setLabel("User removed");
      })
      .catch(() => alert("Something went wrong - try again or refresh the page"));
  }
  return (
    <div className="removeUserTile__root">
      <div className="removeUserTile__user">
        <Avatar
          src={currentUser.avatarUrl}
          alt="user_avatar"
          className="removeUserTile__avatar"
        >
          {currentUser.username?.charAt(0)}
        </Avatar>
        <span className="removeUserTile__name">{currentUser.username}</span>
      </div>
      <Button
        className="removeUserTile__btn"
        size="small"
        disabled={label !== "Remove"}
        onClick={handleClick}
      >
        {label}
      </Button>
      {showAlertRemove && (
        <AlertSnackbar
          severity="info"
          message="User removed from league"
          key={`${currentUser.username}-tile-alert-remove`}
        />
      )}
    </div>
  );
};

export default RemoveUsersPanel;
